import React, { useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import useCommonStore from 'stores/useCommon';
import { formatCurrency, toLocalTime } from 'utils';
import { useGetFolioRevenueReportForExport } from 'hooks/useGetFolioRevenueReport';
import { FolioRevenueReport } from 'services/api/type/report.type';

interface Props {
  fileName: string;
  filterOptions: any;
  t: (key: string) => string;
}

const getMonthsInRange = (checkIn: string, checkOut: string): string[] => {
  const start = dayjs(checkIn).startOf('month');
  const end = dayjs(checkOut).startOf('month');
  const months: string[] = [];
  let current = start;

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    months.push(current.format('YYYY-MM'));
    current = current.add(1, 'month');
  }

  return months;
};

const groupDataByMonth = (items: FolioRevenueReport[]): Record<string, FolioRevenueReport[]> => {
  return items.reduce<Record<string, FolioRevenueReport[]>>((groups, item) => {
    const monthKey = item.invoice_date ? dayjs(item.invoice_date).format('YYYY-MM') : 'unknown';
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(item);
    return groups;
  }, {});
};

const ExportFolioRevenueReport = ({ fileName, filterOptions, t }: Props) => {
  const { currentBranch } = useCommonStore();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [enable, setEnable] = useState<boolean>(false);

  const { data: folioRevenueData } = useGetFolioRevenueReportForExport(filterOptions, enable);

  useEffect(() => {
    setEnable(!isLoadingExport);
  }, [isLoadingExport]);

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';

  const applyRowBorder = (row: ExcelJS.Row, bold = false) => {
    row.eachCell({ includeEmpty: true }, cell => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      if (bold) {
        cell.font = { bold: true };
      }
    });
  };

  const writeTableHeaders = (sheet: ExcelJS.Worksheet, rowNumber: number) => {
    const headers = [
      { cell: `A${rowNumber}`, value: 'STT' },
      { cell: `B${rowNumber}`, value: t('common.bookingData.saleOrderRef') },
      { cell: `C${rowNumber}`, value: t('common.bookingData.guestName') },
      { cell: `D${rowNumber}`, value: t('common.branchName') },
      { cell: `E${rowNumber}`, value: t('common.bookingData.roomType') },
      { cell: `F${rowNumber}`, value: t('bookingPage.roomLock.roomNo') },
      { cell: `G${rowNumber}`, value: t('common.time.checkIn') },
      { cell: `H${rowNumber}`, value: t('common.time.checkOut') },
      { cell: `I${rowNumber}`, value: t('bookingPage.bookingList.source') },
      { cell: `J${rowNumber}`, value: t('managePage.folioRevenueReport.invoiceDate') },
      { cell: `K${rowNumber}`, value: t('managePage.folioRevenueReport.taxExcluded') },
      { cell: `L${rowNumber}`, value: t('managePage.folioRevenueReport.total') }
    ];

    headers.forEach(({ cell, value }) => {
      sheet.getCell(cell).value = value;
      sheet.getCell(cell).font = { bold: true, size: 11 };
      sheet.getCell(cell).alignment = { horizontal: 'center', vertical: 'middle' };
      sheet.getCell(cell).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  };

  const mapItemToRow = (item: FolioRevenueReport, stt: string) => ({
    stt,
    sale_order_ref: String(item.sale_order_ref),
    guest_name: item.guest_name || '',
    branch_name: item.branch_name || '',
    room_type_name: item.room_type_name || '',
    room_no: item.room_no || '',
    check_in: item.check_in ? toLocalTime(item.check_in, 'DD-MM-YYYY HH:mm:ss') : '',
    check_out: item.check_out ? toLocalTime(item.check_out, 'DD-MM-YYYY HH:mm:ss') : '',
    source: item.source || '',
    invoice_date: item.invoice_date ? toLocalTime(item.invoice_date, 'DD-MM-YYYY') : '',
    tax_excluded: formatCurrency(Math.round(item.tax_excluded)),
    total: formatCurrency(Math.round(item.total))
  });

  const exportToCSV = async () => {
    try {
      setIsLoadingExport(true);

      const dataRows = folioRevenueData.filter(item => item.sale_order_ref !== 'TOTAL');
      const grandTotalRow = folioRevenueData.find(item => item.sale_order_ref === 'TOTAL');
      const groupedByMonth = groupDataByMonth(dataRows);
      const monthsInRange = getMonthsInRange(filterOptions.check_in, filterOptions.check_out);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Report');

      sheet.mergeCells('A1:L1');
      sheet.getCell('A1').value = currentBranch?.name?.toUpperCase() || '';
      sheet.getCell('A1').font = { bold: true, size: 16 };

      sheet.mergeCells('A2:L2');
      sheet.getCell('A2').value = `Địa chỉ: ${currentBranch?.address}`;
      sheet.getCell('A2').font = { size: 12 };

      sheet.mergeCells('A3:L3');
      sheet.getCell('A3').value = currentBranch?.phone_contact
        ? `ĐT: ${currentBranch?.phone_contact}`
        : '';
      sheet.getCell('A3').font = { size: 12 };

      sheet.mergeCells('A4:L4');
      sheet.getCell('A4').value = t('managePage.folioRevenueReport.title');
      sheet.getCell('A4').font = { bold: true, size: 16 };
      sheet.getCell('A4').alignment = { horizontal: 'center' };

      sheet.mergeCells('A5:L5');
      sheet.getCell('A5').value =
        `${t('managePage.folioRevenueReport.invoiceDate')}: ${toLocalTime(filterOptions.check_in, 'DD-MM-YYYY')} - ${toLocalTime(filterOptions.check_out, 'DD-MM-YYYY')}`;
      sheet.getCell('A5').font = { bold: true, size: 12 };
      sheet.getCell('A5').alignment = { horizontal: 'center' };

      sheet.columns = [
        { key: 'stt', width: 10, style: { alignment: { horizontal: 'center' } } },
        { key: 'sale_order_ref', width: 15, style: { alignment: { horizontal: 'center' } } },
        { key: 'guest_name', width: 15, style: { alignment: { horizontal: 'center' } } },
        { key: 'branch_name', width: 15, style: { alignment: { horizontal: 'center' } } },
        { key: 'room_type_name', width: 12 },
        { key: 'room_no', width: 10, style: { alignment: { horizontal: 'center' } } },
        { key: 'check_in', width: 20 },
        { key: 'check_out', width: 20 },
        { key: 'source', width: 20 },
        { key: 'invoice_date', width: 15, style: { alignment: { horizontal: 'center' } } },
        { key: 'tax_excluded', width: 17, style: { alignment: { horizontal: 'center' } } },
        { key: 'total', width: 17, style: { alignment: { horizontal: 'center' } } }
      ];

      let globalStt = 0;

      monthsInRange.forEach((monthKey, monthIndex) => {
        const monthItems = (groupedByMonth[monthKey] || []).sort((a, b) =>
          String(a.sale_order_ref).localeCompare(String(b.sale_order_ref))
        );

        if (monthIndex > 0) {
          sheet.addRow([]);
        }

        const titleRowNumber = (sheet.lastRow?.number ?? 5) + 1;
        sheet.mergeCells(`A${titleRowNumber}:L${titleRowNumber}`);
        sheet.getCell(`A${titleRowNumber}`).value =
          `${t('common.time.month')} ${dayjs(monthKey).format('MM/YYYY')}`;
        sheet.getCell(`A${titleRowNumber}`).font = { bold: true, size: 12 };
        sheet.getCell(`A${titleRowNumber}`).alignment = { horizontal: 'left' };

        writeTableHeaders(sheet, titleRowNumber + 1);

        let monthTaxExcluded = 0;
        let monthTotal = 0;

        monthItems.forEach(item => {
          globalStt += 1;
          monthTaxExcluded += item.tax_excluded;
          monthTotal += item.total;

          const row = sheet.addRow(mapItemToRow(item, String(globalStt)));
          applyRowBorder(row);
        });

        const monthTotalRow = sheet.addRow({
          stt: '',
          sale_order_ref: `${t('managePage.folioRevenueReport.monthTotal')} ${dayjs(monthKey).format('MM/YYYY')}`,
          guest_name: '',
          branch_name: '',
          room_type_name: '',
          room_no: '',
          check_in: '',
          check_out: '',
          source: '',
          invoice_date: '',
          tax_excluded: formatCurrency(Math.round(monthTaxExcluded)),
          total: formatCurrency(Math.round(monthTotal))
        });
        applyRowBorder(monthTotalRow, true);
        sheet.addRow([]);
      });

      const grandTotalTaxExcluded =
        grandTotalRow?.tax_excluded ?? dataRows.reduce((sum, item) => sum + item.tax_excluded, 0);
      const grandTotalAmount =
        grandTotalRow?.total ?? dataRows.reduce((sum, item) => sum + item.total, 0);

      const totalRow = sheet.addRow({
        stt: '',
        sale_order_ref: 'TOTAL',
        guest_name: '',
        branch_name: '',
        room_type_name: '',
        room_no: '',
        check_in: '',
        check_out: '',
        source: '',
        invoice_date: '',
        tax_excluded: formatCurrency(Math.round(grandTotalTaxExcluded)),
        total: formatCurrency(Math.round(grandTotalAmount))
      });
      applyRowBorder(totalRow, true);

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (error) {
      console.error('Export Error:', error);
    } finally {
      setIsLoadingExport(false);
    }
  };

  return (
    <Button icon={<DownloadOutlined />} onClick={exportToCSV} loading={isLoadingExport}>
      {t('common.actions.exportData')}
    </Button>
  );
};

export default ExportFolioRevenueReport;
